import { action, computed, observable } from 'mobx'
import { MSLineProps, Series } from '../../components/charts'
import { PROJECT_MAIN_STORE } from '../ProjectMainStore'
import { BRANDS_MAIN_STORE } from '../BrandsMainStore'
import { PRODUCTS_MAIN_STORE } from '../ProductsMainStore'
import {
  BestProductSalesDTOObject,
  OrderStatsRequestDTO,
  ProductDTO,
  ProductTotalPeriodSalesDTO,
  ProductTotalPeriodStatDTO,
  ProjectTotalPeriodSalesDTO,
  ProjectTotalPeriodStatDTO,
  SalesDTOObject,
  TotalPeriodSales,
  TotalPeriodStatDTOObject,
  TotalPeriodStats
} from '../../models'
import { Api, getMainImageSrcForObj, getToFixed2Number } from '../../utilities'
import { RootStore } from '../RootStore'
import { TotalPeriodData, TotalPeriodDataMap } from '../StatisticsOverviewStore'
import moment from 'moment'
import { STATISTICS_VIEWER_STORE } from '../StatisticsViewerStore'
import { ViewersPeriodChartTabs } from './StatisticsViewerPeriodChartStore.types'
import { STATISTICS_VIEWERS_TABLE_STORE } from '../StatisticsViewersTableStore'
import { GENERAL_STORE } from '../GeneralStore'
import { DraggableColLabels } from '../StatsBaseTableStore'
import { FilterDTO } from '../../models/FilterDTO'
import { STATISTICS_PAGE_STORE } from 'store/StatisticsPageStore'

export const STATISTICS_OVERVIEW_PERIOD_CHART_STORE = 'STATISTICS_OVERVIEW_PERIOD_CHART_STORE'
export class StatisticsViewerPeriodChartStore {
  private readonly rootStore: RootStore

  constructor (rootStore: RootStore) {
    this.rootStore = rootStore
  }

  // todo extract duplicated code with stats overview store

  @observable private totalPeriodDataMap: TotalPeriodDataMap = new Map()
  // @observable private bestProducts: BestProductsDTOObject = null
  @observable private bestProductSales: BestProductSalesDTOObject = null
  @observable private selectedViewersPeriodChartTab: ViewersPeriodChartTabs = ViewersPeriodChartTabs.TOTAL_WATCH

  @action private setTotalPeriodDataMap = (
    totalPeriodDataMap: TotalPeriodDataMap
  ) => {
    this.totalPeriodDataMap = totalPeriodDataMap
  }

  @action private setTotalPeriodDataForEntity = (
    entityId: number,
    data: TotalPeriodData
  ): void => {
    this.totalPeriodDataMap.set(entityId, data)
  }

  @action public deleteEntityTotalPeriodData = (entityId: number): void => {
    this.totalPeriodDataMap.delete(entityId)
  }

  @action private setSelectedViewersPeriodChartTab = (tab: ViewersPeriodChartTabs) => {
    this.selectedViewersPeriodChartTab = tab
  }

  @action private setBestSales = (data: BestProductSalesDTOObject) => {
    this.bestProductSales = data
  }

  clearTotalPeriodDataMap = (): void => {
    this.setTotalPeriodDataMap(new Map())
  }

  @computed get hasBest (): boolean {
    // return !(!this.bestProducts && !this.bestProductSales)
    return Boolean(this.bestProductSales)
  }

  @computed get dayLabelKeys (): { label: string }[] {
    const {
      [STATISTICS_VIEWER_STORE]: {
        period
      }
    } = this.rootStore
    const daysCountForSelectedPeriod = moment(period.endDate).diff(moment(period.startDate), 'days')
    if (!daysCountForSelectedPeriod) return null

    const dayLabelKeys: { label: string }[] = []
    for (let i = daysCountForSelectedPeriod; i > 0; i--) {
      dayLabelKeys.push({
        label: moment().subtract(i, 'days').format('DD/MM/YYYY')
      })
    }

    return dayLabelKeys
  }

  @computed get periodChartProps (): MSLineProps {
    if (!this.totalPeriodDataMap.size || !this.dayLabelKeys) return null

    const {
      [PROJECT_MAIN_STORE]: {
        getProjectById
      },
      [BRANDS_MAIN_STORE]: {
        getBrandById
      },
      [PRODUCTS_MAIN_STORE]: {
        getProductById
      },
      [STATISTICS_VIEWERS_TABLE_STORE]: {
        isMainEntityStore,
        isMainEntityProduct,
        isMainEntityProject,
        getEntityGraphColor
        // getEventLabel
      }
    } = this.rootStore

    let yAxisName: string
    let numberPrefix: string
    let feedData: (
      data: { value: number }[],
      periodStats: TotalPeriodStatDTOObject,
      sales: SalesDTOObject
    ) => (dayLabel: { label: string }) => void

    switch (this.selectedViewersPeriodChartTab) {
      case ViewersPeriodChartTabs.TOTAL_WATCH: {
        yAxisName = 'Hours'
        numberPrefix = ''
        feedData = (data, periodStats, _) => ({ label }) => {
          const milliseconds: number = (periodStats[label]?.totalWatchTime || 0)
          const valueAsHours: number = milliseconds
            ? getToFixed2Number(milliseconds / 3600000)
            : 0
          data.push({ value: valueAsHours })
        }
        break
      }
      case ViewersPeriodChartTabs.TOTAL_INTERACTION: {
        yAxisName = 'Hours'
        numberPrefix = ''
        feedData = (data, periodStats, _) => ({ label }) => {
          const milliseconds: number = (periodStats[label]?.totalInteraction || 0)
          const valueAsHours: number = milliseconds
            ? getToFixed2Number(milliseconds / 3600000)
            : 0
          data.push({ value: valueAsHours })
        }
        break
      }
      case ViewersPeriodChartTabs.ADD_REVENUE: {
        yAxisName = 'Add revenue USD'
        numberPrefix = '$'
        feedData = (data, periodStats, sales) => ({ label }) => {
          const value = (periodStats[label]?.revenue || 0) + (sales[label]?.commission || 0)
          data.push({ value })
        }
        break
      }
      case ViewersPeriodChartTabs.PURCHASE_REVENUE: {
        yAxisName = 'Purchase revenue USD'
        numberPrefix = '$'
        feedData = (data, _, sales) => ({ label }) => {
          const value = (sales[label]?.total || 0)
          data.push({ value })
        }
        break
      }
      default: break
    }

    const series: Series[] = []
    Array.from(this.totalPeriodDataMap.entries())
      .forEach(([entityId, { periodStats, sales }]) => {
        const data = []
        const entityDTO = (() => {
          switch (true) {
            case isMainEntityProject: return getProjectById(entityId)
            case isMainEntityStore: return getBrandById(entityId)
            case isMainEntityProduct: return getProductById(entityId)
            default: return null
          }
        })()

        this.dayLabelKeys.forEach(feedData(data, periodStats, sales))

        series.push({
          seriesname: entityDTO?.name,
          anchorBgColor: getEntityGraphColor(entityId),
          color: getEntityGraphColor(entityId),
          image: isMainEntityProduct
            ? getMainImageSrcForObj(entityDTO)
            : undefined,
          data
        })
      })
    let best: Series = null
    if (isMainEntityProduct && this.bestProductSales &&
      this.selectedViewersPeriodChartTab === ViewersPeriodChartTabs.PURCHASE_REVENUE) {
      const data = []
      this.dayLabelKeys.forEach(({ label }) => {
        const bestProductId = this.bestProductSales[label]?.topRevenue?.productId
        const value = this.bestProductSales[label]?.topRevenue?.sales?.total
        const entityObject: ProductDTO = getProductById(bestProductId)
        data.push({
          value,
          entityName: entityObject?.name,
          entityImage: getMainImageSrcForObj(entityObject)
        })
      })

      best = {
        seriesname: null,
        isBest: true,
        anchorBorderColor: '#B9BBC9',
        color: '#ffffff',
        alpha: '0.1',
        anchorBgColor: '#ffffff',
        data
      }
    }
    // const revenueLabel = User.isShopOwner() ? 'Add expenses' : 'Add revenue'

    return {
      yAxisName,
      numberPrefix,
      dataset: series,
      categories: [
        { category: this.dayLabelKeys }
      ],
      best
      // eventsLegend: `${legendLabel}: ${selectedViewerEventsToShowArr.map(getEventLabel).join(', ')}`
    }
  }

  getProductPeriodStats = async (entityId: number): Promise<void> => {
    const {
      [GENERAL_STORE]: {
        setLoadingOn,
        setLoadingOff,
        handleApiError
      },
      [STATISTICS_VIEWERS_TABLE_STORE]: {
        getSelectedCorrespondingEntitySets,
        isMainEntityStore
      },
      [STATISTICS_VIEWER_STORE]: {
        period
      }
    } = this.rootStore

    setLoadingOn()

    const correspondingEntitySets = getSelectedCorrespondingEntitySets(entityId)
    const productIds = isMainEntityStore
      ? [...correspondingEntitySets[DraggableColLabels.PRODUCTS].values()]
      : [entityId]
    const projectIds = [...correspondingEntitySets[DraggableColLabels.PROJECT].values()]

    await Promise.all([
      Api.post<ProductTotalPeriodStatDTO[], FilterDTO>(
        '/stats/summary/totalPeriod/products',
        {
          productIds,
          projectIds,
          ...period
        },
        'statsApi'
      ),
      Api.post<ProductTotalPeriodSalesDTO[], OrderStatsRequestDTO>(
        '/order/statistic/period/products',
        {
          products: productIds,
          projects: projectIds,
          startDate: moment(period.startDate != null ? period.startDate : moment() ).format('YYYY-MM-DD'),
          endDate: moment(period.endDate != null ? period.endDate : moment() ).format('YYYY-MM-DD')
        }
      )
    ])
      .then(data => {
        this.handleTotalPeriodStatsAndSalesData(entityId, data)
        setLoadingOff()
      })
      .catch(handleApiError)
  }

  getProjectPeriodStats = async (projectId: number): Promise<void> => {
    const {
      [GENERAL_STORE]: {
        setLoadingOn,
        setLoadingOff,
        handleApiError
      },
      [STATISTICS_VIEWERS_TABLE_STORE]: {
        getSelectedCorrespondingEntitySets
      },
      [STATISTICS_VIEWER_STORE]: {
        period
      }
    } = this.rootStore

    setLoadingOn()

    const correspondingEntitySets = getSelectedCorrespondingEntitySets(projectId)
    const hasChildren = Boolean(correspondingEntitySets[DraggableColLabels.PROJECT].size)
    let projectIds = [projectId]

    if (hasChildren) {
      projectIds = projectIds.concat(
        [...correspondingEntitySets[DraggableColLabels.PROJECT].values()]
      )
    }

    const productIds = [...correspondingEntitySets[DraggableColLabels.PRODUCTS].values()]

    await Promise.all([
      Api.post<ProjectTotalPeriodStatDTO[], FilterDTO>(
        '/stats/summary/totalPeriod/projects',
        {
          projectIds,
          productIds,
          ...period
          // eventTypes
        },
        'statsApi'
      ),
      Api.post<ProjectTotalPeriodSalesDTO[], OrderStatsRequestDTO>(
        '/order/statistic/period/projects',
        {
          projects: projectIds,
          products: productIds,
          startDate: moment(period.startDate != null ? period.startDate : moment()).format('YYYY-MM-DD'),
          endDate: moment(period.endDate != null ? period.endDate : moment()).format('YYYY-MM-DD')
        }
      )
    ])
      .then(data => {
        this.handleTotalPeriodStatsAndSalesData(projectId, data)
        setLoadingOff()
      })
      .catch(handleApiError)
  }

  getBestProducts = async (): Promise<void> => {
    const {
      [STATISTICS_PAGE_STORE]: {
        selectedStoreIds
      },
      [STATISTICS_VIEWER_STORE]: {
        period
      },
      [GENERAL_STORE]: {
        handleApiError
      }
    } = this.rootStore

    await Api
      .post<BestProductSalesDTOObject, OrderStatsRequestDTO>(
        '/order/statistic/period/products/best',
        {
          brands: selectedStoreIds,
          startDate: moment(period.startDate != null ? period.startDate : moment()).format('YYYY-MM-DD'),
          endDate: moment(period.endDate != null ? period.endDate : moment()).format('YYYY-MM-DD')
        }
      )
      .then(this.setBestSales)
      .catch(handleApiError)
  }

  private handleTotalPeriodStatsAndSalesData = (
    entityId: number,
    data: [ProjectTotalPeriodStatDTO[], ProjectTotalPeriodSalesDTO[]] |
    [ProductTotalPeriodStatDTO[], ProductTotalPeriodSalesDTO[]]
  ) => {
    const parsedData: TotalPeriodData = {
      sales: {},
      periodStats: {}
    }

    const [stats, sales] = data as [TotalPeriodStats[], TotalPeriodSales[]]
    stats.forEach(stat => {
      Object.entries(stat.periodStats).forEach(([timeLabel, periodStat]) => {
        const prevValue = parsedData.periodStats[timeLabel] || {
          count: 0,
          revenue: 0,
          totalInteraction: 0,
          totalWatchTime: 0
        }
        parsedData.periodStats[timeLabel] = {
          ...periodStat,
          count: prevValue.count + periodStat.count,
          revenue: prevValue.revenue + periodStat.revenue,
          totalInteraction: prevValue.totalInteraction + periodStat.totalInteraction,
          totalWatchTime: prevValue.totalWatchTime + periodStat.totalWatchTime
        }
      })
    })
    sales.forEach(sale => {
      Object.entries(sale.sales).forEach(([timeLabel, salesDto]) => {
        const prevValue = parsedData.sales[timeLabel] || {
          addToCart: 0,
          commission: 0,
          count: 0,
          total: 0
        }
        parsedData.sales[timeLabel] = {
          ...salesDto,
          addToCart: prevValue.addToCart + salesDto.addToCart,
          commission: prevValue.commission + salesDto.commission,
          count: prevValue.count + salesDto.count,
          total: prevValue.total + salesDto.total
        }
      })
    })

    this.setTotalPeriodDataForEntity(entityId, parsedData)
  }

  public get chartTabs (): ViewersPeriodChartTabs[] {
    const {
      [STATISTICS_VIEWERS_TABLE_STORE]: {
        isMainEntityProduct
      }
    } = this.rootStore

    return isMainEntityProduct
      ? [
        ViewersPeriodChartTabs.ADD_REVENUE,
        ViewersPeriodChartTabs.PURCHASE_REVENUE
      ]
      : Object.values(ViewersPeriodChartTabs)
  }

  public onSelectedTabChange = (selectedTab: ViewersPeriodChartTabs): void => {
    this.setSelectedViewersPeriodChartTab(selectedTab)
  }
}
